import { useState, useEffect, useRef, useMemo } from 'react'
import { ChevronDownIcon, AlertCircleIcon, CheckIcon } from './Icons'
import { Spinner } from './Spinner'

export interface SelectOption {
  label: string
  value: string
  disabled?: boolean
}

export interface SelectProps {
  /** Label text displayed above or beside the select */
  label: string
  /** Array of options to display in the dropdown */
  options: SelectOption[]
  /** Current selected value (controlled) */
  value: string
  /** Callback when selection changes */
  onChange: (value: string) => void
  /** Placeholder text when no value is selected */
  placeholder?: string
  /** Help text displayed below the select */
  helpText?: string
  /** Error message displayed below the select */
  error?: string | boolean
  /** Disabled state */
  disabled?: boolean
  /** Loading state — shows a spinner and prevents interaction */
  loading?: boolean
  /** Display label inline (left side) instead of above */
  labelInline?: boolean
  /** Custom ID for the select element */
  id?: string
  /** Name attribute for the select element */
  name?: string
  /** Optional data-id for testing */
  'data-id'?: string
  /** Maximum height of the dropdown options list */
  maxHeight?: 'small' | 'medium' | 'default'
  /** Visually hide the label (still accessible to screen readers) */
  labelHidden?: boolean
}

export function Select({
  label,
  options,
  value,
  onChange,
  placeholder,
  helpText,
  error,
  disabled = false,
  loading = false,
  labelInline = false,
  id,
  name: _name,
  'data-id': dataId,
  maxHeight = 'default',
  labelHidden = false,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const optionsListRef = useRef<HTMLDivElement>(null)

  // Generate a unique ID if not provided for accessibility
  const selectId =
    id || `select-${label.replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).substr(2, 9)}`
  const helpTextId = `${selectId}-help`
  const errorTextId = `${selectId}-error`
  const listboxId = `${selectId}-listbox`
  const isError = !!error

  // Max height classes for the dropdown options list
  const maxHeightClasses = {
    small: 'max-h-32', // 128px - fits ~3-4 options
    medium: 'max-h-40', // 160px - fits ~5 options
    default: 'max-h-60', // 240px - current behavior
  }

  // Get selected option display
  const selectedOption = useMemo(() => {
    if (!value) return null
    return options.find((option) => option.value === value)
  }, [value, options])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset highlighted index when dropdown opens/closes
  useEffect(() => {
    if (isOpen) {
      // Set highlighted index to current selected option
      const currentIndex = options.findIndex((opt) => opt.value === value)
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0)
    } else {
      setHighlightedIndex(-1)
    }
  }, [isOpen, options, value])

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsListRef.current) {
      const highlightedElement = optionsListRef.current.children[highlightedIndex] as HTMLElement
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex])

  const handleToggle = () => {
    if (disabled || loading) return
    setIsOpen(!isOpen)
  }

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled || loading) return

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (isOpen && highlightedIndex >= 0 && options[highlightedIndex] && !options[highlightedIndex].disabled) {
          handleSelect(options[highlightedIndex].value)
        } else if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'Escape':
        event.preventDefault()
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        } else {
          // Find next non-disabled option
          let nextIndex = highlightedIndex + 1
          while (nextIndex < options.length && options[nextIndex]?.disabled) {
            nextIndex++
          }
          if (nextIndex < options.length) {
            setHighlightedIndex(nextIndex)
          }
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (isOpen) {
          // Find previous non-disabled option
          let prevIndex = highlightedIndex - 1
          while (prevIndex >= 0 && options[prevIndex]?.disabled) {
            prevIndex--
          }
          if (prevIndex >= 0) {
            setHighlightedIndex(prevIndex)
          }
        }
        break
      case 'Tab':
        // Allow tab to close dropdown and move focus
        if (isOpen) {
          setIsOpen(false)
          setHighlightedIndex(-1)
        }
        break
    }
  }

  // Border and focus styles matching TextField exactly
  const baseBorderColor = error ? 'border-destructive' : 'border-border'
  const focusBorderColor = error ? 'border-destructive' : 'border-ring'

  // Wrapper classes matching TextField
  const wrapperClasses = `
    relative flex items-stretch w-full rounded transition-all duration-150 ease-in-out border
    ${disabled ? 'bg-muted cursor-not-allowed' : 'bg-background'}
    ${isOpen ? `ring-1 ${error ? 'ring-destructive' : 'ring-ring'} ${focusBorderColor}` : baseBorderColor}
  `.trim()

  const renderLabel = () => (
    <label
      htmlFor={selectId}
      className={labelHidden ? 'sr-only' : `block text-[13px] font-medium text-foreground ${labelInline ? 'mr-4 min-w-[120px] pt-2' : 'mb-1'}`}
    >
      {label}
    </label>
  )

  const renderSelect = () => (
    <div className="relative" ref={containerRef}>
      {loading ? (
        <div className="flex items-center justify-center py-3 bg-muted rounded border border-border">
          <Spinner size="small" />
          <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
        </div>
      ) : (
      <>
      {/* Trigger button */}
      <div className={wrapperClasses}>
        <button
          type="button"
          id={selectId}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-invalid={isError}
          aria-describedby={isError ? errorTextId : helpText ? helpTextId : undefined}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={`
            flex-1 w-full bg-transparent border-none outline-none text-left
            disabled:cursor-not-allowed disabled:text-muted-foreground
            px-3 py-2 pr-8 text-[14px] leading-5 h-[36px]
            focus:ring-0 focus:outline-none
          `.trim()}
          data-id={dataId}
        >
          {selectedOption ? (
            <span className="text-foreground">{selectedOption.label}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </button>

        {/* Chevron Icon */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
          <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full bg-background border border-border rounded shadow-lg"
          role="presentation"
        >
          {/* Options list */}
          <div
            ref={optionsListRef}
            role="listbox"
            id={listboxId}
            className={`${maxHeightClasses[maxHeight]} overflow-y-auto py-1`}
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">Nenhuma opção disponível</div>
            ) : (
              options.map((option, index) => {
                const isSelected = value === option.value
                const isHighlighted = highlightedIndex === index

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    onClick={() => !option.disabled && handleSelect(option.value)}
                    onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
                    className={`
                      w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between
                      ${option.disabled
                        ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                        : isSelected
                          ? 'bg-[#F1F8FF] text-blue-600'
                          : isHighlighted
                            ? 'bg-accent text-foreground'
                            : 'text-foreground hover:bg-accent'
                      }
                    `.trim()}
                  >
                    <span className={isSelected ? 'font-medium' : ''}>{option.label}</span>
                    {isSelected && (
                      <CheckIcon className="w-4 h-4 text-blue-600 shrink-0" />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  )

  const renderHelpText = () => {
    if (isError && typeof error === 'string') {
      return (
        <div id={errorTextId} className="mt-1 flex items-center gap-1.5 text-destructive text-xs">
          <AlertCircleIcon className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )
    }

    if (helpText) {
      return (
        <div id={helpTextId} className="mt-1 text-muted-foreground text-xs">
          {helpText}
        </div>
      )
    }

    return null
  }

  if (labelInline) {
    return (
      <div className="flex items-start font-sans">
        {renderLabel()}
        <div className="flex-1">
          {renderSelect()}
          {renderHelpText()}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full font-sans">
      {renderLabel()}
      {renderSelect()}
      {renderHelpText()}
    </div>
  )
}
